import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  CheckCircle2, 
  Brain, 
  ClipboardList, 
  FileCheck, 
  Zap,
  BarChart3,
  Clock,
  Users,
  AlertCircle,
  XCircle,
  HelpCircle,
  Search,
  Target,
  HelpingHand
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "../LandingPage";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

// Map pain point icons
const painPointIcons = [
  <AlertCircle className="w-5 h-5 text-red-500" />,
  <XCircle className="w-5 h-5 text-red-500" />,
  <Clock className="w-5 h-5 text-red-500" />,
  <HelpCircle className="w-5 h-5 text-red-500" />,
  <Search className="w-5 h-5 text-red-500" />,
  <Target className="w-5 h-5 text-red-500" />
];

export default function IndustryPageTemplate({ data }) {
  const {
    industry,
    slug,
    heroTitle,
    heroSubtitle,
    heroDescription,
    painPoints,
    exampleFields,
    exampleIntakeForm,
    aiScoringExplanation,
    useCases,
    ctaText,
    seo,
    relatedIndustries,
    whyAIScoring,
    howItWorks
  } = data;

  // Set document title and meta description for SEO
  useEffect(() => {
    // Set title
    document.title = seo?.title || `${heroTitle} | HeyIntake`;
    
    // Set meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = seo?.description || heroSubtitle;

    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://heyintake.com/intake-form-for-${slug}`;

    return () => {
      document.title = 'HeyIntake | AI-Powered Lead Intake';
    };
  }, [seo, heroTitle, heroSubtitle, slug]);

  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <MarketingNav />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              AI Intake Forms for {industry}
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              {heroTitle}
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              {heroSubtitle}
            </p>
            <p className="text-base text-muted-foreground/80 max-w-2xl mx-auto mb-8">
              {heroDescription}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="rounded-full px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`${slug}-hero-cta`}>
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/demo">
                <Button variant="outline" size="lg" className="rounded-full px-8 py-6 text-base font-medium" data-testid={`${slug}-hero-demo`}>
                  See How It Works
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why AI Scoring Section - NEW for word count */}
      {whyAIScoring && (
        <section className="py-16 px-6 bg-white border-b border-border/30">
          <div className="max-w-4xl mx-auto">
            <motion.div {...fadeIn}>
              <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4 text-center">
                {whyAIScoring.title}
              </h2>
              <p className="text-muted-foreground text-center mb-8 max-w-3xl mx-auto">
                {whyAIScoring.intro}
              </p>
              <div className="prose prose-lg max-w-none text-muted-foreground">
                {whyAIScoring.paragraphs.map((para, i) => (
                  <p key={i} className="mb-4">{para}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Pain Points Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeIn}>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Common Challenges for {industry}
            </h2>
            <p className="text-lg text-muted-foreground">
              Sound familiar? HeyIntake solves these problems automatically.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 h-full rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                    {painPointIcons[index % painPointIcons.length]}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{point.title}</h3>
                  <p className="text-sm text-muted-foreground">{point.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Fields Section */}
      <section className="py-20 px-6 bg-[#f5f5f0]">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeIn}>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Example Intake Form Fields for {industry}
            </h2>
            <p className="text-lg text-muted-foreground">
              Customize these or create your own — HeyIntake's drag-and-drop builder makes it easy.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {exampleFields.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] bg-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground">{category.category}</h3>
                  </div>
                  <ul className="space-y-2">
                    {category.fields.map((field, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        {field}
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Intake Form Section */}
      {exampleIntakeForm && (
        <section className="py-20 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <motion.div className="text-center mb-10" {...fadeIn}>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                {exampleIntakeForm.title}
              </h2>
              <p className="text-lg text-muted-foreground">
                {exampleIntakeForm.description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 rounded-2xl border-none shadow-[0_8px_30px_rgb(0,0,0,0.08)] bg-[#f5f5f0]">
                <div className="space-y-5">
                  {exampleIntakeForm.fields.map((field, index) => (
                    <div key={index} className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        {field.label}
                      </label>
                      {field.type === "select" ? (
                        <div className="w-full p-3 bg-white rounded-xl border border-slate-200 text-sm text-muted-foreground">
                          {field.options?.[0] || "Select..."}
                          <span className="float-right text-slate-400">▼</span>
                        </div>
                      ) : field.type === "textarea" ? (
                        <div className="w-full p-3 bg-white rounded-xl border border-slate-200 text-sm text-muted-foreground h-20">
                          {field.placeholder || "Type here..."}
                        </div>
                      ) : (
                        <div className="w-full p-3 bg-white rounded-xl border border-slate-200 text-sm text-muted-foreground">
                          {field.placeholder || "Enter response..."}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 pt-6 border-t border-slate-200 text-center">
                  <Link to="/register">
                    <Button size="lg" className="rounded-full px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      {exampleIntakeForm.cta}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* AI Scoring Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeIn}>
              <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                AI-Powered Adaptive Scoring
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                {aiScoringExplanation.title}
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                {aiScoringExplanation.description}
              </p>
              <ul className="space-y-3">
                {aiScoringExplanation.factors.map((factor, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-foreground">{factor}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-6 rounded-2xl border-none shadow-[0_8px_30px_rgb(0,0,0,0.08)] bg-[#f5f5f0]">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">AI Score Example</span>
                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-600 text-xs font-semibold">High Priority</span>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center text-white text-2xl font-bold">
                    {aiScoringExplanation.exampleLead.score || 85}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{aiScoringExplanation.exampleLead.name}</h3>
                    <p className="text-sm text-muted-foreground">{aiScoringExplanation.exampleLead.context}</p>
                  </div>
                </div>
                <div className="p-3 bg-white rounded-xl">
                  <p className="text-sm text-foreground italic">
                    "{aiScoringExplanation.exampleLead.summary}"
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section - NEW for word count */}
      {howItWorks && (
        <section className="py-20 px-6 bg-[#f5f5f0]">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-12" {...fadeIn}>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                {howItWorks.title}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {howItWorks.subtitle}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use Cases Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeIn}>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              How {industry} Use HeyIntake
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] bg-[#f5f5f0] h-full">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    {useCase.icon === 'brain' && <Brain className="w-5 h-5 text-primary" />}
                    {useCase.icon === 'zap' && <Zap className="w-5 h-5 text-primary" />}
                    {useCase.icon === 'chart' && <BarChart3 className="w-5 h-5 text-primary" />}
                    {useCase.icon === 'clock' && <Clock className="w-5 h-5 text-primary" />}
                    {useCase.icon === 'users' && <Users className="w-5 h-5 text-primary" />}
                    {useCase.icon === 'file' && <FileCheck className="w-5 h-5 text-primary" />}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{useCase.title}</h3>
                  <p className="text-sm text-muted-foreground">{useCase.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Industries Section - NEW for cross-linking */}
      {relatedIndustries && relatedIndustries.length > 0 && (
        <section className="py-16 px-6 bg-[#f5f5f0] border-t border-border/30">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-8" {...fadeIn}>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                HeyIntake for Other Service Professionals
              </h2>
              <p className="text-muted-foreground">
                Explore how other industries use AI-powered intake forms
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-4">
              {relatedIndustries.map((related, index) => (
                <Link
                  key={index}
                  to={related.path}
                  className="px-5 py-2.5 bg-white rounded-full text-sm font-medium text-foreground hover:bg-primary hover:text-white transition-colors shadow-sm"
                >
                  {related.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Quick Links Section - NEW for internal linking */}
      <section className="py-12 px-6 bg-white border-t border-border/30">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-muted-foreground mb-4">
            Have questions about HeyIntake for {industry.toLowerCase()}?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/faq" className="text-primary hover:underline font-medium">
              View FAQ
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/demo" className="text-primary hover:underline font-medium">
              Watch Demo
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/docs" className="text-primary hover:underline font-medium">
              Documentation
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/" className="text-primary hover:underline font-medium">
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
              {ctaText || `Ready to Streamline Your ${industry} Intake?`}
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Join other {industry.toLowerCase()} using HeyIntake's AI-powered intake forms to qualify leads and onboard clients faster.
            </p>
            <Link to="/register">
              <Button size="lg" variant="secondary" className="rounded-full px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`${slug}-cta-btn`}>
                Start Your Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-white/70">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                7-day trial for $1
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                No credit card required to start
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Cancel anytime
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
